import {
  DESKTOP_BRIDGE_EXPECTED_API_VERSION,
  DESKTOP_BRIDGE_JOB_TIMEOUT_MS,
  DESKTOP_BRIDGE_RETRY_MS,
  DESKTOP_BRIDGE_WS_BASE,
} from './config';
import {
  addDesktopLanPrinter,
  applyDesktopPrinterConfig,
  DesktopBridgeError,
  fetchDesktopPrinters,
  fetchDesktopPrintJob,
  getStoredDesktopBridgeToken,
  printDesktopTestPage,
  probeDesktopBridge,
  removeDesktopPrinter,
  saveDesktopPrinterConfigToBridge,
  setDesktopDefaultPrinter,
  submitDesktopPrintJob,
} from './desktopBridgeClient';
import {
  getDesktopPrinterSettings,
  isDesktopPrintingEligible,
  saveLocalDesktopPrinterConfig,
  subscribeDesktopPrinterSettings,
  updateDesktopPrinterSettings,
} from './desktopPrinterSettings';
import type {
  DesktopBridgeEvent,
  DesktopBridgeReachabilityReason,
  DesktopBridgeVersion,
  DesktopPrinter,
  DesktopPrinterConfig,
  DesktopPrintJob,
} from './types';

export type DesktopBridgeStatus = 'disabled' | 'idle' | 'checking' | 'ready' | 'unreachable';

export type DesktopBridgeSnapshot = {
  status: DesktopBridgeStatus;
  reason: DesktopBridgeReachabilityReason | null;
  version: DesktopBridgeVersion | null;
  printers: DesktopPrinter[];
  defaultPrinterId: string | null;
  /** Printer used for receipts on this browser (preferred → bridge default → only printer). */
  activePrinter: DesktopPrinter | null;
  scanning: boolean;
  wsConnected: boolean;
  lastJob: DesktopPrintJob | null;
};

const INITIAL: DesktopBridgeSnapshot = {
  status: 'idle',
  reason: null,
  version: null,
  printers: [],
  defaultPrinterId: null,
  activePrinter: null,
  scanning: false,
  wsConnected: false,
  lastJob: null,
};

const TERMINAL: ReadonlySet<DesktopPrintJob['status']> = new Set(['completed', 'failed']);

export function pickActiveDesktopPrinter(
  printers: readonly DesktopPrinter[],
  preferredId: string | null,
  defaultId: string | null,
): DesktopPrinter | null {
  return (
    printers.find((p) => p.id === preferredId) ??
    printers.find((p) => p.id === defaultId) ??
    (printers.length === 1 ? printers[0] : null)
  );
}

class DesktopBridgeProviderImpl {
  private snapshot: DesktopBridgeSnapshot = INITIAL;

  private readonly listeners = new Set<() => void>();

  private readonly jobWaiters = new Map<string, (job: DesktopPrintJob) => void>();

  private started = false;

  private probing: Promise<void> | null = null;

  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  private socket: WebSocket | null = null;

  private wsRetryAttempt = 0;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DesktopBridgeSnapshot => this.snapshot;

  private set(patch: Partial<DesktopBridgeSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    next.activePrinter = pickActiveDesktopPrinter(
      next.printers,
      getDesktopPrinterSettings().preferredPrinterId,
      next.defaultPrinterId,
    );
    this.snapshot = next;
    this.listeners.forEach((fn) => {
      try {
        fn();
      } catch {
        /* ignore subscriber errors */
      }
    });
  }

  /** Idempotent; safe to call from every screen that cares about printing. */
  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    subscribeDesktopPrinterSettings(() => this.onSettingsChanged());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.snapshot.status === 'unreachable') void this.probe();
    });
    void this.probe();
  }

  private onSettingsChanged(): void {
    const enabled = isDesktopPrintingEligible() && getDesktopPrinterSettings().enabled;
    if (!enabled && this.snapshot.status !== 'disabled') {
      this.teardown();
      this.set({ ...INITIAL, status: 'disabled' });
      return;
    }
    if (enabled && this.snapshot.status === 'disabled') {
      void this.probe();
      return;
    }
    this.set({});
  }

  private teardown(): void {
    if (this.retryTimer) globalThis.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.socket?.close();
    this.socket = null;
  }

  probe(): Promise<void> {
    if (!isDesktopPrintingEligible() || !getDesktopPrinterSettings().enabled) {
      this.set({ ...INITIAL, status: 'disabled' });
      return Promise.resolve();
    }
    this.probing ??= this.runProbe().finally(() => {
      this.probing = null;
    });
    return this.probing;
  }

  /** Resolves when an in-flight probe (including its printer list load) finishes. */
  settled(): Promise<void> {
    return this.probing ?? Promise.resolve();
  }

  private async runProbe(): Promise<void> {
    if (this.retryTimer) globalThis.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.snapshot.status !== 'ready') this.set({ status: 'checking' });
    const result = await probeDesktopBridge();
    if (!result.reachable) {
      this.teardown();
      this.set({ status: 'unreachable', reason: result.reason, printers: [], defaultPrinterId: null, wsConnected: false });
      this.retryTimer = globalThis.setTimeout(() => void this.probe(), DESKTOP_BRIDGE_RETRY_MS);
      return;
    }
    if (result.version.apiVersion !== DESKTOP_BRIDGE_EXPECTED_API_VERSION) {
      this.set({ status: 'unreachable', reason: 'incompatible', version: result.version, printers: [] });
      return;
    }
    this.set({ status: 'ready', reason: null, version: result.version });
    this.connectSocket();
    try {
      await this.loadPrinters('quick');
      await this.pushPendingConfigs();
    } catch {
      /* bridge went away between calls; next probe recovers */
    }
  }

  private connectSocket(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    const url = new URL(DESKTOP_BRIDGE_WS_BASE);
    url.searchParams.set('token', getStoredDesktopBridgeToken());
    let ws: WebSocket;
    try {
      ws = new WebSocket(url.toString());
    } catch {
      return;
    }
    this.socket = ws;
    ws.onopen = () => {
      this.wsRetryAttempt = 0;
      this.set({ wsConnected: true });
    };
    ws.onmessage = (event) => {
      try {
        this.onEvent(JSON.parse(String(event.data)) as DesktopBridgeEvent);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = () => {
      if (this.socket !== ws) return;
      this.socket = null;
      this.set({ wsConnected: false });
      if (this.snapshot.status !== 'ready') return;
      const delay = Math.min(30_000, 1_000 * 2 ** this.wsRetryAttempt++);
      this.retryTimer = globalThis.setTimeout(() => void this.probe(), delay);
    };
  }

  private onEvent(event: DesktopBridgeEvent): void {
    if (event.type === 'PrintersChanged') {
      const defaultPrinterId = event.printers.find((p) => p.isDefault)?.id ?? null;
      this.set({ printers: event.printers, defaultPrinterId });
    } else if (event.type === 'JobUpdated') {
      this.set({ lastJob: event.job });
      if (TERMINAL.has(event.job.status)) this.jobWaiters.get(event.job.jobId)?.(event.job);
    }
  }

  async loadPrinters(refresh?: 'quick' | 'full'): Promise<DesktopPrinter[]> {
    if (refresh === 'full') this.set({ scanning: true });
    try {
      const list = await fetchDesktopPrinters(refresh);
      this.set({ printers: list.printers, defaultPrinterId: list.defaultPrinterId });
      return list.printers;
    } finally {
      if (refresh === 'full') this.set({ scanning: false });
    }
  }

  private async pushPendingConfigs(): Promise<void> {
    const { pendingSync, configs } = getDesktopPrinterSettings();
    for (const id of pendingSync) {
      const config = configs[id];
      if (!config || !this.snapshot.printers.some((p) => p.id === id)) continue;
      await this.saveConfig(id, config).catch(() => undefined);
    }
  }

  async addLanPrinter(host: string, port?: number, name?: string): Promise<DesktopPrinter> {
    const printer = await addDesktopLanPrinter(host, port, name);
    await this.loadPrinters();
    return printer;
  }

  async removePrinter(printerId: string): Promise<void> {
    await removeDesktopPrinter(printerId);
    if (getDesktopPrinterSettings().preferredPrinterId === printerId) {
      updateDesktopPrinterSettings({ preferredPrinterId: null });
    }
    await this.loadPrinters();
  }

  /** Makes the printer this browser's receipt printer and the bridge default for other tabs. */
  async setDefaultPrinter(printerId: string): Promise<void> {
    updateDesktopPrinterSettings({ preferredPrinterId: printerId });
    const { defaultPrinterId } = await setDesktopDefaultPrinter(printerId);
    this.set({
      defaultPrinterId,
      printers: this.snapshot.printers.map((p) => ({ ...p, isDefault: p.id === defaultPrinterId })),
    });
  }

  /** Saves locally first, then on the bridge; returns whether the bridge accepted it. */
  async saveConfig(printerId: string, config: DesktopPrinterConfig): Promise<{ savedToBridge: boolean; config: DesktopPrinterConfig }> {
    saveLocalDesktopPrinterConfig(printerId, config, false);
    try {
      const saved = await saveDesktopPrinterConfigToBridge(printerId, config);
      saveLocalDesktopPrinterConfig(printerId, saved, true);
      this.set({ printers: this.snapshot.printers.map((p) => (p.id === printerId ? { ...p, config: saved } : p)) });
      return { savedToBridge: true, config: saved };
    } catch {
      return { savedToBridge: false, config };
    }
  }

  async applyConfig(printerId: string): Promise<DesktopPrintJob> {
    return this.waitForJob((await applyDesktopPrinterConfig(printerId)).jobId);
  }

  async testPrint(printerId: string): Promise<DesktopPrintJob> {
    return this.waitForJob((await printDesktopTestPage(printerId)).jobId);
  }

  /** Sends encoded ESC/POS; the bridge adds cut / drawer / beep from the printer config. */
  async print(
    bytes: Uint8Array,
    options: { printerId?: string; jobId?: string; raw?: boolean } = {},
  ): Promise<DesktopPrintJob> {
    const printerId = options.printerId ?? this.snapshot.activePrinter?.id;
    if (!printerId) throw new DesktopBridgeError('bluetoothPrinter.desktop.errors.noPrinter');
    const job = await submitDesktopPrintJob({ printerId, bytes, jobId: options.jobId, raw: options.raw });
    return this.waitForJob(job.jobId);
  }

  private waitForJob(jobId: string): Promise<DesktopPrintJob> {
    return new Promise((resolve, reject) => {
      let poll: ReturnType<typeof setInterval> | null = null;
      const timeout = globalThis.setTimeout(
        () => finish(null, new DesktopBridgeError('bluetoothPrinter.desktop.errors.jobTimeout')),
        DESKTOP_BRIDGE_JOB_TIMEOUT_MS,
      );
      const finish = (job: DesktopPrintJob | null, error?: Error) => {
        globalThis.clearTimeout(timeout);
        if (poll) globalThis.clearInterval(poll);
        this.jobWaiters.delete(jobId);
        if (job?.status === 'failed') reject(new DesktopBridgeError(job.error || 'bluetoothPrinter.desktop.errors.printFailed'));
        else if (job) resolve(job);
        else reject(error);
      };
      this.jobWaiters.set(jobId, (job) => finish(job));
      // Polling covers a dropped WebSocket; the push usually wins.
      const check = () =>
        fetchDesktopPrintJob(jobId)
          .then((job) => TERMINAL.has(job.status) && finish(job))
          .catch(() => undefined);
      poll = globalThis.setInterval(check, this.snapshot.wsConnected ? 2_000 : 500);
      void check();
    });
  }
}

export const desktopBridgeProvider = new DesktopBridgeProviderImpl();
